import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Book, Zap, Shield, Code, ArrowRight } from 'lucide-react'

export const Route = createFileRoute('/docs/')({ 
  component: DocsIndex
})

function DocsIndex() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">API Documentation</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to integrate with our API key management platform. 
            Get started quickly with our comprehensive guides and examples.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
              <CardTitle>Quick Start</CardTitle>
              <CardDescription>
                Get up and running with your first API key in minutes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                <ArrowRight className="w-4 h-4 mr-2" />
                Get Started
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Code className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle>API Reference</CardTitle>
              <CardDescription>
                Complete API documentation with examples and code samples
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                <Book className="w-4 h-4 mr-2" />
                View Reference
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <CardTitle>Authentication</CardTitle>
              <CardDescription>
                Learn how to securely authenticate your API requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                <Shield className="w-4 h-4 mr-2" />
                Learn More
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle>Popular Guides</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <a href="/docs/getting-started" className="block p-4 border rounded-lg hover:bg-gray-50">
                  <h3 className="font-medium text-gray-900">Getting Started</h3>
                  <p className="text-sm text-gray-600">Create your first API key and make your first request</p>
                </a>
                <a href="/docs/pricing" className="block p-4 border rounded-lg hover:bg-gray-50">
                  <h3 className="font-medium text-gray-900">Pricing & Plans</h3>
                  <p className="text-sm text-gray-600">Understand our pricing model and choose the right plan</p>
                </a>
                <a href="/docs/faq" className="block p-4 border rounded-lg hover:bg-gray-50">
                  <h3 className="font-medium text-gray-900">FAQ</h3>
                  <p className="text-sm text-gray-600">Frequently asked questions and troubleshooting</p>
                </a>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Code Examples</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-900 rounded-lg p-4 text-sm">
                <pre className="text-green-400">
{`curl -X GET "https://api.example.com/v1/data" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}
                </pre>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                Replace <code className="bg-gray-100 px-1 rounded">YOUR_API_KEY</code> with your actual API key from the dashboard.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  )
}